import React, { useEffect, useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Stack, TextField, MenuItem, Autocomplete, Alert,
    Typography, IconButton, Box,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { CertCategory } from '../../models/worker';
import {
    createManualCert,
    fetchCertNameSuggestions,
    CreateManualCertPayload,
    ALLOWED_MANUAL_ATTACHMENT_EXTENSIONS,
    MAX_MANUAL_ATTACHMENTS,
} from '../../api/workerCertificationsApi';

interface AddManualCertModalProps {
    open: boolean;
    workerId: number;
    onClose: () => void;
    onCreated: () => void;
}

const AddManualCertModal: React.FC<AddManualCertModalProps> = ({ open, workerId, onClose, onCreated }) => {
    const [proposedCertName, setProposedCertName] = useState('');
    const [issuer, setIssuer] = useState('');
    const [certCategory, setCertCategory] = useState<CertCategory | ''>('');
    const [expiryDate, setExpiryDate] = useState('');
    const [level, setLevel] = useState('');
    const [certNumber, setCertNumber] = useState('');
    const [attachedImages, setAttachedImages] = useState<File[]>([]);
    const [nameOptions, setNameOptions] = useState<string[]>([]);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (open) {
            setProposedCertName('');
            setIssuer('');
            setCertCategory('');
            setExpiryDate('');
            setLevel('');
            setCertNumber('');
            setAttachedImages([]);
            setError(null);
            fetchCertNameSuggestions()
                .then(rows => setNameOptions(rows.map(r => r.proposedCertName)))
                .catch(() => setNameOptions([]));
        }
    }, [open]);

    const handleFilesPicked = (e: React.ChangeEvent<HTMLInputElement>) => {
        const picked = Array.from(e.target.files ?? []);
        const room = MAX_MANUAL_ATTACHMENTS - attachedImages.length;
        if (picked.length > room) {
            setError(`Max ${MAX_MANUAL_ATTACHMENTS} images. ${room} slot(s) left.`);
            return;
        }
        setAttachedImages(prev => [...prev, ...picked]);
        // Allow re-picking the same file later (browsers cache the input value).
        e.target.value = '';
    };

    const removeImage = (idx: number) => {
        setAttachedImages(prev => prev.filter((_, i) => i !== idx));
    };

    const handleSubmit = async () => {
        if (!proposedCertName.trim()) {
            setError('Cert name is required.');
            return;
        }
        setBusy(true);
        setError(null);
        try {
            const payload: CreateManualCertPayload = {
                workerId,
                proposedCertName: proposedCertName.trim(),
                issuer: issuer.trim() || undefined,
                certCategory: (certCategory || undefined) as CertCategory | undefined,
                expiryDate: expiryDate || undefined,
                level: level.trim() || undefined,
                certNumber: certNumber.trim() || undefined,
            };
            await createManualCert(payload, attachedImages);
            onCreated();
            onClose();
        } catch (e: any) {
            setError(e?.response?.data?.message ?? e?.message ?? 'Failed to create cert.');
        } finally {
            setBusy(false);
        }
    };

    const hasImages = attachedImages.length > 0;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Add certification manually</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    {error && <Alert severity="error">{error}</Alert>}
                    <Alert severity={hasImages ? 'info' : 'warning'}>
                        {hasImages ? (
                            <>
                                {attachedImages.length} image(s) attached — this cert will land as
                                <strong> Pending review</strong> with a stitched PDF source.
                            </>
                        ) : (
                            <>
                                No images attached — this cert will be saved as
                                <strong> Unverified</strong> and won't appear on the CV until
                                a recruiter approves it or you upload supporting evidence.
                            </>
                        )}
                    </Alert>
                    <Autocomplete
                        freeSolo
                        options={nameOptions}
                        value={proposedCertName}
                        onInputChange={(_, v) => setProposedCertName(v)}
                        renderInput={params => (
                            <TextField {...params} label="Cert name" required autoFocus size="small" />
                        )}
                    />
                    <TextField
                        label="Issuer"
                        value={issuer}
                        onChange={e => setIssuer(e.target.value)}
                        size="small"
                    />
                    <TextField
                        label="Category"
                        value={certCategory}
                        onChange={e => setCertCategory(e.target.value as CertCategory | '')}
                        select
                        size="small"
                    >
                        <MenuItem value="">(none)</MenuItem>
                        {Object.values(CertCategory).map(c => (
                            <MenuItem key={c} value={c}>{c}</MenuItem>
                        ))}
                    </TextField>
                    <Stack direction="row" spacing={2}>
                        <TextField
                            label="Cert number"
                            value={certNumber}
                            onChange={e => setCertNumber(e.target.value)}
                            size="small"
                            fullWidth
                        />
                        <TextField
                            label="Level"
                            value={level}
                            onChange={e => setLevel(e.target.value)}
                            size="small"
                            fullWidth
                        />
                    </Stack>
                    <Stack direction="row" spacing={2}>
                        <TextField
                            label="Expiry date"
                            type="date"
                            value={expiryDate}
                            onChange={e => setExpiryDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                            fullWidth
                        />
                    </Stack>

                    <Box>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                            <Button
                                variant="outlined"
                                size="small"
                                component="label"
                                disabled={attachedImages.length >= MAX_MANUAL_ATTACHMENTS}
                            >
                                Attach image{attachedImages.length > 0 ? 's' : ''}
                                <input
                                    type="file"
                                    hidden
                                    multiple
                                    accept={ALLOWED_MANUAL_ATTACHMENT_EXTENSIONS.join(',')}
                                    onChange={handleFilesPicked}
                                />
                            </Button>
                            <Typography variant="caption" color="text.secondary">
                                {attachedImages.length}/{MAX_MANUAL_ATTACHMENTS} (images only — use Upload for PDFs)
                            </Typography>
                        </Stack>
                        {attachedImages.length > 0 && (
                            <Stack spacing={0.5}>
                                {attachedImages.map((f, i) => (
                                    <Stack
                                        key={`${f.name}-${i}`}
                                        direction="row"
                                        alignItems="center"
                                        spacing={1}
                                        sx={{ pl: 1 }}
                                    >
                                        <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {f.name} <Typography component="span" variant="caption" color="text.secondary">
                                                ({(f.size / 1024).toFixed(0)} KB)
                                            </Typography>
                                        </Typography>
                                        <IconButton size="small" onClick={() => removeImage(i)} aria-label="remove">
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Stack>
                                ))}
                            </Stack>
                        )}
                    </Box>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={busy}>Cancel</Button>
                <Button onClick={handleSubmit} disabled={busy} variant="contained">
                    {busy ? 'Saving…' : hasImages ? `Save with ${attachedImages.length} image(s)` : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddManualCertModal;
